import React, { useState, useRef, useEffect, useMemo } from "react"
import { BiChevronDown } from "react-icons/bi"
import { generateConfigUrl } from "./urlConfig"
import { getTranslations } from "./components/translations"
import { materialManager } from "./materialManager"

// Helper function to get proper image URL
const getImageUrl = (thumbnail) => {
  if (!thumbnail) return null
  if (thumbnail.startsWith("#")) return thumbnail
  if (thumbnail.startsWith("http") || thumbnail.startsWith("//"))
    return thumbnail
  const cleanPath = thumbnail.replace(/^\.\//, "")
  return `https://moroso-ayea.ensoul.worsk/${cleanPath}`
}

// Component for individual related product box
const RelatedProductBox = ({
  product,
  materialConfig,
  imageUrl,
  allMaterials,
  language,
  translations,
}) => {
  // Get localized product name
  const getLocalizedName = () => {
    return product?.lang?.[language] || product?.name || ""
  }

  // Get material thumbnails for the configured materials
  const materialThumbnails = useMemo(() => {
    if (!materialConfig || !allMaterials) return []

    return Object.entries(materialConfig).map(([type, slug]) => {
      // Find material in all materials
      const material = allMaterials.find(
        (m) => m.slug === slug || m.name?.toLowerCase() === slug?.toLowerCase(),
      )
      return {
        type,
        thumbnail: material?.thumbnail
          ? getImageUrl(material.thumbnail)
          : "#ccc",
        name: material?.name || slug,
      }
    })
  }, [materialConfig, allMaterials])

  // Handle click - navigate to product with config
  const handleClick = () => {
    // Build materials object for URL generation
    const materialsForUrl = {}
    if (materialConfig) {
      Object.entries(materialConfig).forEach(([type, slug]) => {
        materialsForUrl[type] = { slug }
      })
    }
    const url = generateConfigUrl(product.code, materialsForUrl, true)
    window.location.href = url
  }

  // Use custom image if provided, otherwise fallback to Emersya
  const productImageSrc = imageUrl || ``

  return (
    <div
      onClick={handleClick}
      className="ay-flex ay-flex-row ay-rounded-3xl ay-cursor-pointer hover:ay-shadow-[0_-5px_5px_rgba(0,0,0,0.10)] ay-transition-[box-shadow] ay-duration-500 ay-bg-gray-50"
    >
      {/* Top - Product image */}
      <div className="ay-w-2/5 ay-bg-[#edeeed] ay-flex ay-rounded-l-3xl ay-items-center ay-justify-center ay-p-4 ay-aspect-square">
        <img
          src={productImageSrc}
          alt={getLocalizedName()}
          className="ay-w-full ay-h-full ay-object-cover"
          onError={(e) => {
            e.target.style.display = "none"
          }}
        />
      </div>

      {/* Bottom - Product info */}
      <div className="ay-flex ay-w-3/5 ay-flex-col">
        {/* Row 1: Name + Price - on same line */}
        <div className="ay-flex ay-justify-between ay-items-center ay-px-4 ay-py-3 ay-gap-2">
          <div className="ay-font-medium ay-text-xs ay-truncate">
            {getLocalizedName()}
          </div>
          <div className="ay-font-semibold ay-text-sm ay-whitespace-nowrap">
            {product.price?.toLocaleString("it-IT")} EUR
          </div>
        </div>

        {/* Separator */}
        <div className="ay-h-px ay-bg-black ay-mx-4" />

        {/* Row 2: Code + Material thumbnails */}
        <div className="ay-flex ay-items-center ay-gap-2 ay-px-4 ay-py-3">
          <span className="ay-text-[10px] ay-text-gray-500 ay-font-mono">
            {product.code}
          </span>
          <div className="ay-flex ay-gap-1.5 ay-ml-auto ay-flex-wrap ay-items-center">
            {materialThumbnails.map((mat, i) => (
              <div
                key={i}
                className="ay-w-5 ay-h-5  ay-border ay-border-gray-300 ay-flex-shrink-0"
                style={{
                  backgroundColor: mat.thumbnail?.startsWith("#")
                    ? mat.thumbnail
                    : undefined,
                  backgroundImage:
                    mat.thumbnail && !mat.thumbnail.startsWith("#")
                      ? `url(${mat.thumbnail})`
                      : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
                title={`${mat.type}: ${mat.name}`}
              />
            ))}
          </div>
        </div>

        {/* Separator */}
        <div className="ay-h-px ay-bg-black ay-mx-4" />

        {/* Row 3: Delivery time */}
        <div className="ay-px-4 ay-py-3">
          <span className="ay-text-xs ay-text-gray-500">
            {translations?.infoSection?.deliveryTime || "10-12 settimane"}
          </span>
        </div>

        {/* Separator */}
        <div className="ay-h-px ay-bg-black ay-mx-4" />

        {/* Row 4: Add to cart button */}
        <div className="ay-px-4 ay-py-3">
          <button
            className="ay-w-full ay-bg-black ay-text-white ay-py-2 ay-rounded-full ay-text-xs ay-uppercase ay-tracking-wider hover:ay-bg-gray-800 ay-transition-colors ay-font-serif"
            onClick={(e) => {
              e.stopPropagation()
              // Could dispatch cart event here
              handleClick()
            }}
          >
            {translations?.infoSection?.addToCart || "Aggiungi al carrello"}
          </button>
        </div>
      </div>
    </div>
  )
}

// Main InfoSection component
const InfoSection = ({
  isVisible,
  onClose,
  selectedProduct,
  products,
  language = "it",
  isMobile = false,
}) => {
  const [usageAccordionOpen, setUsageAccordionOpen] = useState(true)
  const [relatedAccordionOpen, setRelatedAccordionOpen] = useState(true)
  const [usageContentHeight, setUsageContentHeight] = useState(0)
  const [relatedContentHeight, setRelatedContentHeight] = useState(0)

  const usageContentRef = useRef(null)
  const relatedContentRef = useRef(null)

  const translations = getTranslations(language)

  // Get all materials from material manager - use state to handle async loading
  const [allMaterials, setAllMaterials] = useState(materialManager.allMaterials || [])

  // Update materials when they become available (async loading)
  useEffect(() => {
    // Check if materials are already loaded
    if (materialManager.allMaterials?.length > 0) {
      setAllMaterials(materialManager.allMaterials)
      return
    }

    // Poll until materials are loaded (they load async from API)
    const checkMaterials = setInterval(() => {
      if (materialManager.allMaterials?.length > 0) {
        setAllMaterials(materialManager.allMaterials)
        clearInterval(checkMaterials)
      }
    }, 200)

    return () => clearInterval(checkMaterials)
  }, [selectedProduct])

  // Update content heights for animation
  useEffect(() => {
    if (usageAccordionOpen && usageContentRef.current) {
      setUsageContentHeight(usageContentRef.current.scrollHeight)
    } else {
      setUsageContentHeight(0)
    }
  }, [usageAccordionOpen, selectedProduct])

  useEffect(() => {
    if (relatedAccordionOpen && relatedContentRef.current) {
      setRelatedContentHeight(relatedContentRef.current.scrollHeight)
    } else {
      setRelatedContentHeight(0)
    }
  }, [relatedAccordionOpen, selectedProduct, products])

  // Reset accordion states when product changes
  useEffect(() => {
    setUsageAccordionOpen(true)
    setRelatedAccordionOpen(true)
  }, [selectedProduct?.code])

  // Get related products data - MUST be before any conditional returns
  const relatedProductsData = useMemo(() => {
    if (!selectedProduct?.relatedProducts || !products) return []

    return selectedProduct.relatedProducts
      .map((config) => {
        const product = products.find((p) => p.code === config.code)
        if (!product) return null
        return {
          product,
          materialConfig: config.materials || {},
          imageUrl: config.imageUrl || null,
        }
      })
      .filter(Boolean)
  }, [selectedProduct?.relatedProducts, products])

  // Check if section should be visible
  const hasUsageImages =
    selectedProduct?.usageImages?.filter(Boolean).length > 0
  const hasRelatedProducts = selectedProduct?.relatedProducts?.length > 0

  // Don't render if no data
  if (!hasUsageImages && !hasRelatedProducts) {
    return null
  }

  return (
    <div
      className={`ay-bg-white ay-relative ${isMobile ? "ay-min-h-0" : "ay-min-h-[90vh]"}`}
    >
      {/* Usage Images - Slider on mobile, accordion grid on desktop */}
      {hasUsageImages && (
        <div className="ay-w-full">
          {/* Mobile: Horizontal scroll slider showing ~1.2 images */}
          <div className="md:ay-hidden ay-py-4">
            <div
              className="ay-flex ay-gap-3 ay-overflow-x-auto ay-scrollbar-hide ay-px-4 ay-scroll-smooth"
              style={{
                scrollSnapType: "x mandatory",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {selectedProduct.usageImages?.map(
                (imgUrl, index) =>
                  imgUrl && (
                    <div
                      key={index}
                      className="ay-flex-shrink-0"
                      style={{
                        width: "calc(83.333% - 8px)",
                        scrollSnapAlign: "start",
                      }}
                    >
                      <img
                        src={imgUrl}
                        alt={`Usage ${index + 1}`}
                        className="ay-w-full ay-aspect-[4/3] ay-object-cover ay-rounded-lg"
                      />
                    </div>
                  ),
              )}
            </div>
          </div>

          {/* Desktop: Accordion with grid */}
          <div className="ay-hidden md:ay-block ay-border-b ay-border-gray-200">
            <button
              onClick={() => setUsageAccordionOpen(!usageAccordionOpen)}
              className="ay-w-full ay-py-4 ay-px-5 ay-flex ay-items-center ay-justify-between hover:ay-bg-gray-50 ay-transition-colors"
            >
              <span className="ay-text-sm ay-font-medium ay-uppercase ay-tracking-wide">
                {translations?.infoSection?.usageImages ||
                  "Come potresti utilizzare le immagini"}
              </span>
              <BiChevronDown
                className={`ay-text-xl ay-transition-transform ay-duration-300 ${
                  usageAccordionOpen ? "ay-rotate-180" : ""
                }`}
              />
            </button>

            <div
              ref={usageContentRef}
              className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
              style={{ maxHeight: `${usageContentHeight}px` }}
            >
              <div className="ay-grid ay-grid-cols-2 lg:ay-grid-cols-3 ay-gap-4 ay-p-5">
                {selectedProduct.usageImages?.map(
                  (imgUrl, index) =>
                    imgUrl && (
                      <img
                        key={index}
                        src={imgUrl}
                        alt={`Usage ${index + 1}`}
                        className="ay-w-full ay-aspect-[4/3] ay-object-cover ay-rounded"
                      />
                    ),
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accordion 2: Related Products */}
      {hasRelatedProducts && relatedProductsData.length > 0 && (
        <div className="ay-w-full ay-border-b ay-border-gray-200">
          <button
            onClick={() => setRelatedAccordionOpen(!relatedAccordionOpen)}
            className="ay-w-full ay-py-4 ay-px-5 ay-flex ay-items-center ay-justify-between hover:ay-bg-gray-50 ay-transition-colors"
          >
            <span className="ay-text-sm ay-font-medium ay-uppercase ay-tracking-wide">
              {translations?.infoSection?.relatedProducts ||
                "Potrebbe piacerti anche"}
            </span>
            <BiChevronDown
              className={`ay-text-xl ay-transition-transform ay-duration-300 ${
                relatedAccordionOpen ? "ay-rotate-180" : ""
              }`}
            />
          </button>

          <div
            ref={relatedContentRef}
            className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
            style={{ maxHeight: `${relatedContentHeight}px` }}
          >
            <div className="ay-grid ay-grid-cols-1 sm:ay-grid-cols-2 lg:ay-grid-cols-4 ay-gap-4 ay-p-5">
              {relatedProductsData.map(
                ({ product, materialConfig, imageUrl }, index) => (
                  <RelatedProductBox
                    key={index}
                    product={product}
                    materialConfig={materialConfig}
                    imageUrl={imageUrl}
                    allMaterials={allMaterials}
                    language={language}
                    translations={translations}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InfoSection
